import React, { useMemo } from 'react';
import { getWebGLSupport } from '../utils/webgl';

// What someone can do when their browser can't start 3D graphics.
export const WebGLFixSteps = ({ linkColor = '#9fc4ff' }) => (
  <>
    <div style={{ fontWeight: 700, margin: '12px 0 6px' }}>To fix it</div>
    <ol style={{ margin: 0, paddingLeft: 20, listStyle: 'decimal' }}>
      <li style={{ marginBottom: 6 }}>
        Close every browser window and open it again. After a graphics hiccup, browsers can switch 3D off for a site until they restart.
      </li>
      <li style={{ marginBottom: 6 }}>
        Turn on hardware acceleration. Chrome or Edge: Settings &rarr; System &rarr; &ldquo;Use graphics acceleration when available&rdquo;, then relaunch.
        Firefox: Settings &rarr; General &rarr; Performance.
      </li>
      <li style={{ marginBottom: 6 }}>Update your browser and your graphics drivers.</li>
      <li>
        Open{' '}
        <a href="https://get.webgl.org/webgl2/" target="_blank" rel="noopener noreferrer" style={{ color: linkColor }}>get.webgl.org/webgl2</a>.
        If it doesn&rsquo;t show a spinning cube, this browser can&rsquo;t run the 3D tests; try another browser.
      </li>
    </ol>
  </>
);

// Shown over a 3D module when its scene can't be drawn (see utils/webgl.js),
// instead of a blank screen. The run it interrupts isn't scored.
const SceneUnavailableNotice = ({ onExit, exitLabel = 'Exit test' }) => {
  const support = useMemo(() => getWebGLSupport(), []);
  const webglOneOnly = support.webgl1 && !support.webgl2;

  return (
    <div
      role="alertdialog"
      aria-labelledby="scene-unavailable-title"
      data-testid="scene-unavailable"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        background: 'rgba(0, 0, 24, 0.94)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        overflowY: 'auto'
      }}
    >
      <div style={{ background: '#000030', border: '1px solid #4444AA', color: '#ffffff', maxWidth: 520, width: '100%', fontFamily: 'Arial, sans-serif' }}>
        <div id="scene-unavailable-title" style={{ background: '#0000B0', padding: '8px 14px', fontWeight: 700, fontSize: 14, textAlign: 'center' }}>
          3D graphics aren&rsquo;t working in this browser
        </div>
        <div style={{ padding: '16px 18px', fontSize: 13, lineHeight: 1.55, color: '#dce6ff' }}>
          <p style={{ margin: '0 0 10px' }}>
            This test draws its scene in 3D (WebGL), and your browser couldn&rsquo;t start it, so the scene can&rsquo;t be shown.
            This run won&rsquo;t be scored.
          </p>
          {webglOneOnly && (
            <p style={{ margin: '0 0 10px' }} data-testid="scene-unavailable-webgl1">
              Your browser has only the older WebGL&nbsp;1. These tests need WebGL&nbsp;2.
            </p>
          )}
          <WebGLFixSteps />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              onClick={onExit}
              data-testid="scene-unavailable-exit"
              style={{ background: '#0000A0', color: '#ffffff', border: '1px solid #4444AA', padding: '8px 14px', cursor: 'pointer', fontWeight: 700 }}
            >
              {exitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SceneUnavailableNotice;
